/**
 * Stage 2b chunk 3 — login reconcile (the read-switch).
 *
 * When a logged-in user opens the app, reconcile each synced key between the
 * cloud and localStorage, then let the app read the reconciled localStorage.
 *
 * PER-KEY rule (the critical data-safety invariant):
 *   1. cloud HAS data for the key  -> cloud is authoritative: hydrate it into
 *      localStorage (replacing the local value for that key).
 *   2. cloud is EMPTY but local HAS data -> seed: push local -> cloud (reusing
 *      the chunk-2 sync) and leave localStorage as-is.
 *   3. both empty -> do nothing.
 * This prevents an empty cloud CATEGORY (e.g. no saved plans yet) from wiping
 * local data that simply hasn't synced. We never do a blanket "pull everything".
 *
 * For the two collapsed single-row tables (strategy_drafts -> 3 keys,
 * user_preferences -> 8 keys) the rule is applied at SUB-KEY granularity, so a
 * cloud blob that omits one sub-key never wipes that local sub-key.
 *
 * Fault tolerance: every cloud fetch is wrapped; on failure we log and keep the
 * existing localStorage value. The app stays fully usable offline.
 *
 * Hydration writes go through rawSetItem/rawRemoveItem (the UNWRAPPED mutators),
 * so pulling cloud -> local does not echo straight back up. The chunk-2
 * write-through stays active for genuine user edits afterwards.
 */
import { SYNC_KEYS, CLOUD_TABLES } from './syncKeys';
import { snapshotsTable } from './snapshots';
import { strategyDraftsTable } from './strategyDrafts';
import { userPreferencesTable, PREFERENCE_CODECS } from './userPreferences';
import { pushKeyNow, rawSetItem, rawRemoveItem } from './writeThrough';
import { getCacheOwner, setCacheOwner, clearAllSyncedLocal } from './localCache';

/**
 * Fired once after the reconcile has hydrated localStorage. The few data readers
 * that initialise ABOVE the auth render-gate (usePortfolio, StrategyContext,
 * ThemeContext, CurrencyContext) listen for this and re-read localStorage, so
 * cloud values take effect without a reload. Components mounted BEHIND the gate
 * read the reconciled localStorage on mount and need no listener.
 */
export const CLOUD_DATA_HYDRATED_EVENT = 'mycryptoplanner:clouddatahydrated';

/** One-time, per-device safety net written before the first hydrate write. */
const BACKUP_KEY = '__premigration_backup';

const DRAFT_KEYS = Object.entries(SYNC_KEYS)
  .filter(([, d]) => d.shape === 'draft')
  .map(([k]) => k);
const PREFERENCE_KEYS = Object.entries(SYNC_KEYS)
  .filter(([, d]) => d.shape === 'preference')
  .map(([k]) => k);

// ── Run-once guard (per user, survives route changes / gate remounts) ─────────
let reconcileState: { userId: string; done: boolean; promise: Promise<void> } | null = null;

/** True once the reconcile has finished for this user (so route changes don't re-run). */
export function isReconcileDone(userId: string | null): boolean {
  return !!userId && reconcileState?.userId === userId && reconcileState.done;
}

/**
 * Clear the run-once guard so the NEXT login re-runs the reconcile. Called on
 * logout: since logout clears the local cache, the same user logging back in must
 * re-hydrate from the cloud rather than being treated as already-reconciled.
 */
export function resetReconcile(): void {
  reconcileState = null;
}

/**
 * Run the reconcile exactly once per user per app load. Repeat calls return the
 * same in-flight/settled promise. Never rejects — failures fall back to local.
 */
export function reconcileOnce(userId: string): Promise<void> {
  if (reconcileState && reconcileState.userId === userId) return reconcileState.promise;
  const state: { userId: string; done: boolean; promise: Promise<void> } = {
    userId,
    done: false,
    promise: Promise.resolve(),
  };
  state.promise = (async () => {
    try {
      await runReconcile(userId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[cloud-sync] reconcile failed; continuing with local data', err);
    } finally {
      state.done = true;
    }
  })();
  reconcileState = state;
  return state.promise;
}

// ── Backup ────────────────────────────────────────────────────────────────────
function backupOnce(): void {
  try {
    if (window.localStorage.getItem(BACKUP_KEY) != null) return; // already backed up
    const keys: Record<string, string | null> = {};
    for (const key of Object.keys(SYNC_KEYS)) {
      keys[key] = window.localStorage.getItem(key);
    }
    // Written via the unwrapped setter so it never tries to sync (it isn't a
    // synced key anyway). Insurance only — nothing reads this back automatically.
    rawSetItem(BACKUP_KEY, JSON.stringify({ at: new Date().toISOString(), keys }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[cloud-sync] pre-reconcile backup failed; proceeding', err);
  }
}

// ── Local helpers ──────────────────────────────────────────────────────────────
function localArrayLength(key: string): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function localHas(key: string): boolean {
  try {
    return window.localStorage.getItem(key) != null;
  } catch {
    return false;
  }
}

// ── The reconcile ───────────────────────────────────────────────────────────────
async function runReconcile(userId: string): Promise<void> {
  // Snapshot every synced key BEFORE the first hydrate write touches anything.
  backupOnce();

  // Account-switch guard: if the cached data is owned by a DIFFERENT user, discard
  // the whole synced cache before reconciling so nothing from the prior user is
  // shown or seeded upward. A null owner (first run after this change, or a fresh
  // device) is treated as belonging to the current user, so existing single-user
  // installs keep their data. `allowSeed` gates the cloud-empty -> push-local-up
  // branch: we never seed a cache that belongs to someone else.
  const owner = getCacheOwner();
  const ownerMismatch = owner != null && owner !== userId;
  if (ownerMismatch) clearAllSyncedLocal();
  const allowSeed = !ownerMismatch;

  const tasks: Promise<void>[] = [];
  for (const [key, d] of Object.entries(SYNC_KEYS)) {
    if (d.shape === 'multi') tasks.push(reconcileMulti(key, d.table, userId, allowSeed));
    else if (d.shape === 'snapshots') tasks.push(reconcileSnapshots(key, userId, allowSeed));
  }
  // The two collapsed single-row tables are reconciled once each (sub-key aware).
  tasks.push(reconcileDrafts(userId, allowSeed));
  tasks.push(reconcilePreferences(userId, allowSeed));

  await Promise.all(tasks);

  // Stamp ownership so a later login by a different user triggers the discard above.
  setCacheOwner(userId);

  // Tell the above-the-gate readers to re-read the reconciled localStorage.
  try {
    window.dispatchEvent(new Event(CLOUD_DATA_HYDRATED_EVENT));
  } catch {
    /* ignore (non-browser env) */
  }
}

/** Array-backed table: cloud has rows -> hydrate; else seed local -> cloud. */
async function reconcileMulti(key: string, table: string, userId: string, allowSeed: boolean): Promise<void> {
  const api = CLOUD_TABLES[table as keyof typeof CLOUD_TABLES] as any;
  let cloud: any[];
  try {
    cloud = await api.fetchAll(userId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[cloud-sync] fetch failed for "${key}"; keeping local`, err);
    return;
  }
  if (Array.isArray(cloud) && cloud.length > 0) {
    rawSetItem(key, JSON.stringify(cloud)); // cloud authoritative
  } else if (allowSeed && localArrayLength(key) > 0) {
    await pushKeyNow(key); // seed local -> cloud (never throws)
  }
}

/** Snapshots: cloud has rows -> hydrate; else seed local -> cloud. */
async function reconcileSnapshots(key: string, userId: string, allowSeed: boolean): Promise<void> {
  let cloud;
  try {
    cloud = await snapshotsTable.fetchAll(userId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[cloud-sync] fetch failed for "${key}"; keeping local`, err);
    return;
  }
  if (Array.isArray(cloud) && cloud.length > 0) {
    rawSetItem(key, JSON.stringify(cloud));
  } else if (allowSeed && localArrayLength(key) > 0) {
    await pushKeyNow(key);
  }
}

/**
 * strategy_drafts: one cloud row holds 3 drafts. Per sub-key: cloud field
 * present -> hydrate that local key; cloud field empty but local present -> seed.
 */
async function reconcileDrafts(userId: string, allowSeed: boolean): Promise<void> {
  let cloud;
  try {
    cloud = await strategyDraftsTable.fetch(userId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[cloud-sync] fetch failed for strategy drafts; keeping local', err);
    return;
  }
  let needSeed = false;
  for (const key of DRAFT_KEYS) {
    const d = SYNC_KEYS[key];
    if (d.shape !== 'draft') continue;
    const cloudVal = cloud ? (cloud as any)[d.field] : null;
    if (cloudVal != null) {
      rawSetItem(key, JSON.stringify(cloudVal)); // cloud authoritative for this draft
    } else if (localHas(key)) {
      needSeed = true; // local-only draft -> seed it up
    }
  }
  // Seeding rebuilds the whole row from current localStorage (incl. any just
  // hydrated siblings), so one push covers every local-only draft.
  if (needSeed && allowSeed && DRAFT_KEYS.length > 0) await pushKeyNow(DRAFT_KEYS[0]);
}

/**
 * user_preferences: one cloud blob holds 8 settings. Per sub-key: cloud has it ->
 * hydrate that local key via the codec; cloud omits it but local has it -> seed.
 */
async function reconcilePreferences(userId: string, allowSeed: boolean): Promise<void> {
  let cloud;
  try {
    cloud = await userPreferencesTable.fetch(userId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[cloud-sync] fetch failed for preferences; keeping local', err);
    return;
  }
  let needSeed = false;
  for (const key of PREFERENCE_KEYS) {
    const d = SYNC_KEYS[key];
    if (d.shape !== 'preference') continue;
    const codec = PREFERENCE_CODECS[key];
    if (!codec) continue;
    const present =
      !!cloud &&
      Object.prototype.hasOwnProperty.call(cloud, d.prefKey) &&
      (cloud as any)[d.prefKey] !== undefined;
    if (present) {
      const raw = codec.decode((cloud as any)[d.prefKey]); // typed -> raw string | null
      if (raw == null) rawRemoveItem(key);
      else rawSetItem(key, raw);
    } else if (localHas(key)) {
      needSeed = true;
    }
  }
  if (needSeed && allowSeed && PREFERENCE_KEYS.length > 0) await pushKeyNow(PREFERENCE_KEYS[0]);
}
